// Who may edit one field of a role.
//
// Two rights govern a role since GOUV-15 (08/08/2026): canEditRole follows the
// governance mode (and, in Agile, the role's holder), while the work fields
// (checklist, indicators, notes) stay open to the holder in every mode, strict
// included. This is the one place that maps a field to the right that governs
// it, so the panel cannot disagree with itself field by field.
//
// The write path itself is single and lives in the org context: every role
// write goes to the backend, which re-checks the very same rule.
import { CircleContext } from '@/circle/contexts/CIrcleContext'
import { useOrgEditActions } from '@/org/contexts/OrgContext'
import { RoleFragment } from '@gql'
import { useCallback, useContext } from 'react'
import { RoleWriteScopeContext } from '../contexts/RoleWriteScopeContext'
import { getRoleFieldWrite } from '../roleFieldWrite'

export interface RoleFieldEdition {
  editable: boolean
  save: (value: string) => Promise<void>
}

export default function useSaveRoleField(
  role: RoleFragment,
  field: keyof RoleFragment
): RoleFieldEdition {
  const { updateRole } = useOrgEditActions()
  // May be absent: this hook must be callable before the caller bails out on a
  // missing circle context, as the rules of hooks demand.
  const circleContext = useContext(CircleContext)

  // The meeting step, if any, that wraps this field (REU04-D12). It is read
  // here, inside the step, and handed to updateRole with the save (#170).
  const writeScope = useContext(RoleWriteScopeContext)
  const { editable, meetingId } = getRoleFieldWrite({
    field: field as string,
    canEditRole: !!circleContext?.canEditRole,
    canEditRoleWorkFields: !!circleContext?.canEditRoleWorkFields,
    writeScope,
  })

  const save = useCallback(
    async (value: string) => {
      if (!editable) return
      await updateRole(role, { [field]: value }, { meetingId })
    },
    [editable, role, field, meetingId, updateRole]
  )

  return { editable, save }
}
